import { Provider, useSelector, useDispatch } from 'react-redux';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useNavigate } from 'react-router-dom';
import { store } from './store';
import { VideoPlayer } from './components/VideoPlayer/VideoPlayer';
import { Playlist } from './components/Sidebar/Playlist';
import { ThemeToggle } from './components/ThemeToggle';
import { RootState } from './store';
import { UserMenu } from './components/Auth/UserMenu';
import { Footer } from './components/Layout/Footer';
import { Contact } from './pages/Contact';
import { Privacy } from './pages/Privacy';
import { Terms } from './pages/Terms';
import { FAQ } from './pages/FAQ';
import Features from './pages/Features';
import Sharing from './pages/Sharing';
import FreePlaylist from './pages/FreePlaylist';
import ChromeExtensionPrivacy from './pages/ChromeExtensionPrivacy';
import { ScrollToTop } from './components/ScrollToTop';
import logo from './assets/logo.svg';
import { useState, useEffect, useRef } from 'react';
import { User, Upload, Link as LinkIcon, Tv, Youtube as YouTube } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { AutoImport } from './components/PlaylistImport/AutoImport';
import { UrlPlaylistImport } from './components/PlaylistImport/UrlPlaylistImport';
import { ClerkProvider } from "@clerk/clerk-react";
import { clerkConfig, CLERK_PUBLISHABLE_KEY } from "./config/clerk";
import SignInPage from "./pages/SignIn";
import SignUpPage from "./pages/SignUp";
import { LanguageIcon } from './components/LanguageIcon';
import { LanguageModal } from './components/LanguageModal';
import { useLanguage } from './hooks/useLanguage';
import { useTranslation } from 'react-i18next';
import {
  enUS,
  esES,
  frFR,
  deDE,
  itIT,
  ptBR,
  arSA,
  nlNL,
  ruRU,
  trTR,
  plPL,
  idID
} from '@clerk/localizations';
import type { LocalizationResource } from '@clerk/types';
import logger from './utils/logger';
import { AuthStateManager } from './components/Auth/AuthStateManager';
import { PlaylistSyncManager } from './components/Auth/PlaylistSyncManager';
import { PlaylistImport } from './components/PlaylistImport/PlaylistImport';
import { LinkInput } from './components/PlaylistImport/LinkInput';
import { PlaylistDetectionProvider } from './contexts/PlaylistDetectionContext';

if (!CLERK_PUBLISHABLE_KEY) {
  throw new Error("Missing Clerk Publishable Key");
}

// Map our language codes to Clerk's localization objects
const LANGUAGE_MAP: Record<string, LocalizationResource> = {
  en: enUS,
  es: esES,
  fr: frFR,
  de: deDE,
  it: itIT,
  pt: ptBR,
  ar: arSA,
  nl: nlNL,
  ru: ruRU,
  tr: trTR,
  pl: plPL,
  id: idID
};

function WelcomeScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showImportModal, setShowImportModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showUrlModal, setShowUrlModal] = useState(false);
  const [playlistUrlToImport, setPlaylistUrlToImport] = useState<string | null>(null);
  const playlistUrlRef = useRef<HTMLInputElement>(null);
  
  // Function to handle when user clicks to import from file
  const handleImportClick = () => {
    setShowImportModal(true);
    // Store the tab preference so that it persists after modal closes
    localStorage.setItem('activePlaylistTab', 'add');
  };
  
  // Function to handle when user clicks to add playlist URL
  const handleUrlImportClick = () => {
    setShowUrlModal(true);
  };
  
  // Function to handle when user clicks to add direct stream
  const handleAddStreamClick = () => {
    setShowLinkModal(true);
    // Store the tab preference so that it persists after modal closes
    localStorage.setItem('activePlaylistTab', 'add');
  };
  
  // Function to close the modals
  const handleCloseModals = () => {
    setShowImportModal(false);
    setShowLinkModal(false);
    setShowUrlModal(false);
  };
  
  // Add this useEffect to handle the imported URL
  useEffect(() => {
    if (playlistUrlToImport) {
      // Set active tab preference explicitly
      localStorage.setItem('activePlaylistTab', 'channels');
      
      // Use React Router's navigate function instead of window.location.href
      // This prevents a full page reload
      navigate(`/?playlist=${encodeURIComponent(playlistUrlToImport)}&source=welcome-screen&tab=channels`);
      
      // Reset the URL state
      setPlaylistUrlToImport(null);
    }
  }, [playlistUrlToImport, navigate]);
  
  return (
    <div className="w-full aspect-video bg-black dark:bg-gray-800 flex items-center justify-center">
      {/* Mobile version (simple) */}
      <div className="text-center text-white p-4 md:hidden">
        <img 
          src={logo} 
          alt={t('altText.logo')} 
          className="w-12 h-12 mx-auto mb-2"
        />
        <h2 className="text-lg font-bold mb-1">
          {t('player.welcome')}
        </h2>
        <p className="text-xs text-gray-400 px-4">
          {t('player.addFirstStream', 'Add your first stream using the sidebar to get started')}
        </p>
      </div>
      
      {/* Desktop version (enhanced with buttons) */}
      <div className="text-center text-white p-8 max-w-3xl hidden md:block">
        <img 
          src={logo} 
          alt={t('altText.logo')} 
          className="w-24 h-24 mx-auto mb-4"
        />
        <h2 className="text-2xl font-bold mb-4">
          {t('player.welcome')}
        </h2>
        <p className="text-base text-gray-300 mb-6">
          {t('player.welcomeDescription', 'To get started, you need to add a streaming source. You can import an M3U playlist file, add a playlist URL, or input a direct stream link.')}
        </p>
        
        <div className="grid grid-cols-3 gap-4 max-w-2xl mx-auto">
          {/* Import M3U file option */}
          <button
            onClick={handleImportClick}
            className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg p-4 transition-colors flex flex-col items-center justify-center"
          >
            <Upload className="w-6 h-6 mb-2" />
            <span className="font-medium">{t('player.importFile', 'Import M3U File')}</span>
            <span className="text-xs text-blue-200 mt-1">{t('player.importFileHint', '.m3u, .m3u8')}</span>
          </button>
          
          {/* Add by URL option */}
          <button
            onClick={handleUrlImportClick}
            className="bg-green-600 hover:bg-green-700 text-white rounded-lg p-4 transition-colors flex flex-col items-center justify-center"
          >
            <LinkIcon className="w-6 h-6 mb-2" />
            <span className="font-medium">{t('player.importPlaylist', 'Add Playlist URL')}</span>
            <span className="text-xs text-green-200 mt-1">{t('player.importPlaylistHint', 'M3U playlist link')}</span>
          </button>
          
          {/* Add direct stream option */}
          <button
            onClick={handleAddStreamClick}
            className="bg-purple-600 hover:bg-purple-700 text-white rounded-lg p-4 transition-colors flex flex-col items-center justify-center"
          >
            <Tv className="w-6 h-6 mb-2" />
            <span className="font-medium">{t('player.addStream', 'Add Direct Stream')}</span>
            <span className="text-xs text-purple-200 mt-1">{t('player.addStreamHint', 'HLS, DASH, MP4')}</span>
          </button>
        </div>
        
        <p className="text-sm text-gray-400 mt-6">
          {t('player.supportedFormats', 'Supported formats: HLS (.m3u8), DASH (.mpd), MP4, and other HTTPS streams')}
        </p>

        <button
          onClick={() => window.open('https://www.youtube.com/watch?v=xCLlILvLLdk', '_blank')}
          className="flex items-center space-x-4 mt-6 px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors duration-200 w-full max-w-md mx-auto"
        >
          <YouTube className="w-20 h-20 shrink-0" />
          <div className="flex flex-col justify-center flex-1">
            <span className="text-lg font-bold">{t('player.watchTutorial', 'Watch Our Tutorial')}</span>
            <span className="text-sm opacity-90">{t('player.tutorialDescription', 'Learn how to import playlists, edit channels & use player controls')}</span>
          </div>
        </button>
      </div>
      
      {/* Conditionally render modals */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">{t('player.importFile', 'Import M3U File')}</h3>
            <PlaylistImport onImportSuccess={handleCloseModals} />
            <button 
              onClick={handleCloseModals}
              className="mt-4 w-full py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              {t('common.cancel', 'Cancel')}
            </button>
          </div>
        </div>
      )}
      
      {showUrlModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-white mb-4">{t('player.importPlaylist', 'Add Playlist URL')}</h3>
            
            <div className="mb-4">
              <label htmlFor="playlistUrl" className="block text-sm font-medium text-gray-300 mb-2">
                {t('player.playlistUrl', 'Playlist URL')}
              </label>
              <input
                type="text"
                id="playlistUrl"
                className="w-full p-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                placeholder="https://example.com/playlist.m3u8"
                ref={playlistUrlRef}
              />
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCloseModals}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-colors"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={() => {
                  const url = playlistUrlRef.current?.value;
                  if (url) {
                    // Instead of redirecting with window.location, use the UrlPlaylistImport component
                    // by setting a state variable and passing the URL to it
                    setPlaylistUrlToImport(url);
                    handleCloseModals();
                  }
                }}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors"
              >
                {t('common.import', 'Import')}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {showLinkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">{t('player.addStream', 'Add Direct Stream')}</h3>
            <LinkInput onEditComplete={handleCloseModals} />
            <button 
              onClick={handleCloseModals}
              className="mt-4 w-full py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              {t('common.cancel', 'Cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function MainContent() {
  const navigate = useNavigate();
  const { currentChannel } = useSelector((state: RootState) => state.player);
  const { t } = useTranslation();
  
  // Get auth state from Redux if available
  const authState = useSelector((state: RootState) => state.auth);
  const isLoggedIn = authState?.user !== null;
  logger.debug('[DEBUG] MainContent auth state', { authState, isLoggedIn });
  
  // Get any URL parameters that might trigger playlist import
  const location = window.location;
  logger.debug('[DEBUG] Current location', { href: location.href });
  logger.debug('[DEBUG] URL search params', { search: location.search });

  return (
    <div className="flex flex-col gap-6">
      <div className="w-screen -mx-4 sm:-mx-6 md:w-full md:mx-0 sticky z-40 bg-gray-100 dark:bg-gray-900 md:static max-w-[100dvw] box-border"
           style={{
             top: '72px',
             marginTop: '-97px',
             paddingTop: '25px',
             paddingBottom: '10px'
           }}>
        <div className="rounded-none md:rounded-lg overflow-hidden aspect-video 
                        bg-black dark:bg-gray-800
                        [&_.video-container]:!h-full [&_.video-container]:!flex 
                        [&_.video-container]:!items-center [&_.video-container]:!justify-center
                        md:mx-1 max-w-[100dvw] box-border">
          {currentChannel ? (
            <VideoPlayer url={currentChannel.url} />
          ) : (
            <WelcomeScreen />
          )}
        </div>
      </div>
      <div className="flex flex-col md:flex-row gap-6 max-w-[100vw]">
        <div className="order-2 md:order-1 flex-1 bg-white dark:bg-gray-800 rounded-lg p-4 md:p-6 text-gray-900 dark:text-gray-300 font-medium leading-relaxed mx-4 md:mx-0">
          <section className="hero mb-12">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4">{t('metadata.home.heading')}</h1>
            <h2 className="text-xl md:text-2xl lg:text-3xl mb-6">{t('metadata.home.subheading')}</h2>
            <p className="text-lg mb-8">
              {t('metadata.home.intro')} {' '}
                <Link to="/m3u-hls-player-features" className="text-blue-500 hover:text-blue-600 transition-colors">
              {t('metadata.home.editorLink')}
              </Link>{' '}
               {t('metadata.home.features')}
            </p>
          </section>

          <section className="features space-y-12">
            <div>
              <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold mb-4">
                {t('metadata.home.managementHeading')}
              </h2>
              <p className="mb-6">
                {t('metadata.home.managementIntro')} {' '}
                  <Link to="/free-m3u-hls-playlists" className="text-blue-500 hover:text-blue-600 transition-colors">
                {t('metadata.home.legalSourcesLink')}
                </Link>{' '}
                {t('metadata.home.managementDetails')} {' '}
                <Link to="/m3u-hls-player-playlist-sharing" className="text-blue-500 hover:text-blue-600 transition-colors">
                  {t('metadata.home.sharingLink')}
                </Link>
                {t('metadata.home.managementConclusion')}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                <div className="text-center p-4 rounded-lg bg-slate-50 dark:bg-gray-700">
                  <h3 className="font-semibold mb-2">{t('metadata.home.importFeatureTitle')}</h3>
                  <p className="text-sm">{t('metadata.home.importFeatureDesc')}</p>
                </div>
                <div className="text-center p-4 rounded-lg bg-slate-50 dark:bg-gray-700">
                  <h3 className="font-semibold mb-2">{t('metadata.home.editFeatureTitle')}</h3>
                  <p className="text-sm">{t('metadata.home.editFeatureDesc')}</p>
                </div>
                <div className="text-center p-4 rounded-lg bg-slate-50 dark:bg-gray-700">
                  <h3 className="font-semibold mb-2">{t('metadata.home.exportFeatureTitle')}</h3>
                  <p className="text-sm">{t('metadata.home.exportFeatureDesc')}</p>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold mb-4">{t('metadata.home.technologyHeading')}</h2>
              <p className="mb-6">
                {t('metadata.home.technologyDesc')}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                <div className="text-center p-4 rounded-lg bg-slate-50 dark:bg-gray-700">
                  <h3 className="font-semibold mb-2">{t('metadata.home.hlsFeatureTitle')}</h3>
                  <p className="text-sm">{t('metadata.home.hlsFeatureDesc')}</p>
                </div>
                <div className="text-center p-4 rounded-lg bg-slate-50 dark:bg-gray-700">
                  <h3 className="font-semibold mb-2">{t('metadata.home.dashFeatureTitle')}</h3>
                  <p className="text-sm">{t('metadata.home.dashFeatureDesc')}</p>
                </div>
                <div className="text-center p-4 rounded-lg bg-slate-50 dark:bg-gray-700">
                  <h3 className="font-semibold mb-2">{t('metadata.home.compatibilityFeatureTitle')}</h3>
                  <p className="text-sm">{t('metadata.home.compatibilityFeatureDesc')}</p>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold mb-4">{t('metadata.home.cloudHeading')}</h2>
              <p className="mb-6">
                {t('metadata.home.cloudDesc')}
              </p>
            </div>

            <div>
              <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold mb-4">{t('metadata.home.exportHeading')}</h2>
              <p className="mb-6">
                {t('metadata.home.exportDesc')}
              </p>
            </div>
          </section>

          <section className="call-to-action mt-12 text-center">
            <h2 className="text-xl md:text-2xl lg:text-3xl font-bold mb-4">{t('metadata.home.ctaHeading')}</h2>
            <p className="mb-6">
              {t('metadata.home.ctaDesc')}
            </p>
            <button 
              onClick={() => navigate('/sign-in')}
              className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
            >
              {t('metadata.home.ctaButton')}
            </button>
          </section>
        </div>
        <div className="order-1 md:order-2 w-full md:w-80 shrink-0 px-4 sm:px-6 md:px-0">
          <Playlist />
        </div>
      </div>
    </div>
  );
}

function AppContent() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);
  const { user } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentLanguage, changeLanguage } = useLanguage();
  
  // Get localization for clerk based on current language
  const getClerkLocalization = (): LocalizationResource | undefined => {
    // Get the base language code (e.g. 'en' from 'en-US')
    const baseLanguage = currentLanguage.split('-')[0];
    
    // Return the matching localization or undefined if not found
    return LANGUAGE_MAP[baseLanguage];
  };
  
  return (
    <div className="min-h-screen flex flex-col bg-gray-100 dark:bg-gray-900">
      {/* Sticky Header */}
      <header className="sticky top-0 z-50 h-[72px] bg-white/95 dark:bg-gray-800/95 backdrop-blur-md shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
          <div className="flex justify-between items-center h-full py-4">
            {/* Logo - Now standalone */}
            <Link to="/" className="flex items-center gap-2 text-xl sm:text-2xl font-bold text-gray-900 dark:text-white hover:text-primary dark:hover:text-primary-dark transition-colors">
              <img src={logo} alt="IPTVPlayer.stream Logo" className="w-8 h-8" />
              <span className="hidden sm:inline">IPTVPlayer.stream</span>
              <span className="sm:hidden">IPTV Player</span>
            </Link>

            <div className="flex items-center gap-4">
              {/* Desktop Menu Items */}
              <div className="hidden md:flex items-center gap-4">
                <UserMenu />
                <ThemeToggle />
                <LanguageIcon onClick={() => setIsLanguageModalOpen(true)} />
              </div>

              {/* Mobile Menu Button - Moved to right */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 text-gray-500 dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label="Toggle menu"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>

            {/* Mobile Menu Dropdown */}
            {isMobileMenuOpen && (
              <div className="absolute md:hidden top-16 right-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg py-3 px-4 space-y-4 z-50 min-w-[200px] border border-gray-200 dark:border-gray-700">
                <div className="flex flex-col space-y-4">
                  <ThemeToggle onToggle={() => setIsMobileMenuOpen(false)} />
                  <LanguageIcon onClick={() => {
                    setIsLanguageModalOpen(true);
                    setIsMobileMenuOpen(false);
                  }} />
                  {user && (
                    <>
                      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                        <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                          <User className="w-4 h-4 mr-2" />
                          {user.email}
                        </div>
                      </div>
                      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                        <UserMenu isMobile onAction={() => setIsMobileMenuOpen(false)} />
                      </div>
                    </>
                  )}
                  {!user && (
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                      <UserMenu isMobile onAction={() => setIsMobileMenuOpen(false)} />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Language Modal */}
      <LanguageModal
        isOpen={isLanguageModalOpen}
        onClose={() => setIsLanguageModalOpen(false)}
      />

      {/* Click outside to close mobile menu */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/50 md:hidden" 
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Main content with adjusted padding */}
      <main className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:pt-8"
            style={{
              paddingTop: 'calc(72px + 25px)' // Header height + scroll spacing
            }}>
        <Helmet>
          {/* Primary Meta Tags */}
          <title>{t('metadata.home.title')}</title>
          <meta name="title" content={t('metadata.home.title')} />
          <meta name="description" content={t('metadata.home.description')} />
          <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
          
          {/* Open Graph / Facebook */}
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://iptvplayer.stream/" />
          <meta property="og:title" content={t('metadata.home.title')} />
          <meta property="og:description" content={t('metadata.home.description')} />
          <meta property="og:image" content="https://iptvplayer.stream/og-image.jpg" />
          
          {/* Twitter */}
          <meta property="twitter:card" content="summary_large_image" />
          <meta property="twitter:url" content="https://iptvplayer.stream/" />
          <meta property="twitter:title" content={t('metadata.home.title')} />
          <meta property="twitter:description" content={t('metadata.home.description')} />
          <meta property="twitter:image" content="https://iptvplayer.stream/og-image.jpg" />
        </Helmet>
        <Routes>
          <Route path="/" element={
            <>
              <UrlPlaylistImport />
              <MainContent />
            </>
          } />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/faq" element={<Navigate to="/m3u-hls-player-faq" replace />} />
          <Route path="/m3u-hls-player-faq" element={<FAQ />} />
          <Route path="/m3u-hls-player-features" element={<Features />} />
          <Route path="/sharing" element={<Navigate to="/m3u-hls-player-playlist-sharing" replace />} />
          <Route path="/m3u-hls-player-sharing" element={<Navigate to="/m3u-hls-player-playlist-sharing" replace />} />
          <Route path="/m3u-hls-player-playlist-sharing" element={<Sharing />} />
          <Route path="/free-m3u-hls-playlists" element={<FreePlaylist />} />
          <Route path="/chrome-extension-privacy-policy" element={<ChromeExtensionPrivacy />} />
          <Route path="/import/:shareId" element={<AutoImport />} />
          <Route path="/sign-in/*" element={<SignInPage />} />
          <Route path="/sign-up/*" element={<SignUpPage />} />
          <Route path="/verify-email" element={<SignUpPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      <Footer />
    </div>
  );
}

function App() {
  return (
    <Provider store={store}>
      <ClerkProvider
        publishableKey={CLERK_PUBLISHABLE_KEY}
        appearance={clerkConfig.appearance}
      >
        <PlaylistDetectionProvider>
          <Router>
            <ScrollToTop />
            <AuthStateManager />
            <PlaylistSyncManager />
            <AppContent />
          </Router>
        </PlaylistDetectionProvider>
      </ClerkProvider>
    </Provider>
  );
}

export default App;
